import { useState, useEffect } from 'react'
import { Save, RefreshCw, AlertCircle, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import useAppConfigStore from '@/stores/appConfigStore'

const GeneralSettingsPage = () => {
  const {
    systemConfig,
    availableCountries,
    availableCurrencies,
    loading,
    error,
    fetchSystemConfig,
    fetchAvailableCountries,
    fetchAvailableCurrencies,
    updateSystemConfig,
    clearError
  } = useAppConfigStore()

  const [formData, setFormData] = useState({
    default_currency: '',
    default_country: '',
    default_language: 'fr',
    timezone: 'Europe/Paris',
    tax_calculation: 'inclusive' as 'inclusive' | 'exclusive'
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [demoMode, setDemoMode] = useState(false)

  // Initialiser les données
  useEffect(() => {
    const initializeData = async () => {
      try {
        await Promise.all([
          fetchSystemConfig(),
          fetchAvailableCountries(),
          fetchAvailableCurrencies()
        ])
      } catch (error) {
        console.warn('Backend non disponible, mode démo activé', error)
        setDemoMode(true)
        // Données de démonstration
        setFormData({
          default_currency: demoCurrencies.length > 0 ? demoCurrencies[0].code : '',
          default_country: demoCountries.length > 0 ? demoCountries[0].code : '',
          default_language: 'fr',
          timezone: 'Europe/Paris',
          tax_calculation: 'inclusive'
        })
      }
    }
    initializeData()
  }, [fetchSystemConfig, fetchAvailableCountries, fetchAvailableCurrencies])

  // Mettre à jour le formulaire quand la configuration système est chargée
  useEffect(() => {
    if (systemConfig) {
      setFormData({
        default_currency: systemConfig.default_currency || '',
        default_country: systemConfig.default_country || '',
        default_language: systemConfig.default_language || 'fr',
        timezone: systemConfig.timezone || 'Europe/Paris',
        tax_calculation: systemConfig.tax_calculation || 'inclusive'
      })
    }
  }, [systemConfig])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    if (saveSuccess) setSaveSuccess(false)
    clearError()
  }

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target
    handleInputChange(name, value)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSaving(true)
    setSaveSuccess(false)
    clearError()

    try {
      if (demoMode) {
        // Mode démo - simuler une sauvegarde réussie
        await new Promise(resolve => setTimeout(resolve, 1000))
        console.log('Configuration sauvegardée (mode démo):', formData)
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 3000)
      } else {
        await updateSystemConfig(formData)
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 3000)
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRefresh = async () => {
    if (demoMode) {
      // Mode démo - recharger les données simulées
      setFormData({
        default_currency: demoCurrencies.length > 0 ? demoCurrencies[0].code : '',
        default_country: demoCountries.length > 0 ? demoCountries[0].code : '',
        default_language: 'fr',
        timezone: 'Europe/Paris',
        tax_calculation: 'inclusive'
      })
      setSaveSuccess(false)
      clearError()
    } else {
      await Promise.all([
        fetchSystemConfig(),
        fetchAvailableCountries(),
        fetchAvailableCurrencies()
      ])
      setSaveSuccess(false)
      clearError()
    }
  }

  // Options pour les langues
  const languageOptions = [
    { value: 'fr', label: 'Français' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'de', label: 'Deutsch' }
  ]

  // Options pour les fuseaux horaires
  const timezoneOptions = [
    { value: 'Europe/Paris', label: 'Central European Time (Paris)' },
    { value: 'Europe/London', label: 'Greenwich Mean Time (London)' },
    { value: 'America/New_York', label: 'Eastern Time (New York)' },
    { value: 'America/Toronto', label: 'Eastern Time (Toronto)' },
    { value: 'America/Chicago', label: 'Central Time (Chicago)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (Los Angeles)' },
    { value: 'Asia/Tokyo', label: 'Japan Standard Time (Tokyo)' }
  ]

  // Données de démonstration si le backend n'est pas disponible
  const demoCountries = [
    { id: '1', code: 'FR', name: 'France', flag_emoji: '🇫🇷', default_currency: 'EUR' },
    { id: '2', code: 'CA', name: 'Canada', flag_emoji: '🇨🇦', default_currency: 'CAD' },
    { id: '3', code: 'US', name: 'United States', flag_emoji: '🇺🇸', default_currency: 'USD' },
    { id: '4', code: 'GB', name: 'United Kingdom', flag_emoji: '🇬🇧', default_currency: 'GBP' }
  ]

  const demoCurrencies = [
    { id: '1', code: 'EUR', symbol: '€', name: 'Euro' },
    { id: '2', code: 'CAD', symbol: 'CA$', name: 'Canadian Dollar' },
    { id: '3', code: 'USD', symbol: '$', name: 'US Dollar' },
    { id: '4', code: 'GBP', symbol: '£', name: 'British Pound' }
  ]

  const countriesToShow = demoMode ? demoCountries : availableCountries
  const currenciesToShow = demoMode ? demoCurrencies : availableCurrencies

  if (loading && !systemConfig) {
    return (
      <div className="animate-in">
        <div className="mb-8">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-full mb-4" />
                <Skeleton className="h-10 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="animate-in">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Paramètres généraux de l'application</h1>
        <p className="text-gray-600 mt-2">
          Configurez les paramètres globaux de l'application, y compris la devise par défaut, le pays par défaut et les options de calcul des taxes.
        </p>
      </div>

      {demoMode && (
        <Alert className="mb-6 bg-yellow-50 border-yellow-200">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            Mode démo activé - Le service de configuration backend n'est pas disponible.
            Les modifications seront simulées localement.
          </AlertDescription>
        </Alert>
      )}

      {error && !demoMode && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {saveSuccess && (
        <Alert className="mb-6 bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800">
            {demoMode ? 'Paramètres sauvegardés en mode démo' : 'Les paramètres ont été sauvegardés avec succès.'}
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Configuration de base */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration de base</CardTitle>
              <CardDescription>
                Paramètres fondamentaux de l'application
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="default_country" className="block text-sm font-medium text-gray-700">
                  Pays par défaut
                </label>
                <select
                  id="default_country"
                  name="default_country"
                  value={formData.default_country}
                  onChange={handleSelectChange}
                  disabled={(loading && !demoMode) || isSaving}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Sélectionner un pays</option>
                  {countriesToShow.map((country) => (
                    <option key={country.id} value={country.code}>
                      {country.flag_emoji} {country.name} ({country.code})
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500">
                  Le pays utilisé par défaut pour les nouveaux utilisateurs
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="default_currency" className="block text-sm font-medium text-gray-700">
                  Devise par défaut
                </label>
                <select
                  id="default_currency"
                  name="default_currency"
                  value={formData.default_currency}
                  onChange={handleSelectChange}
                  disabled={(loading && !demoMode) || isSaving}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Sélectionner une devise</option>
                  {currenciesToShow.map((currency) => (
                    <option key={currency.id} value={currency.code}>
                      {currency.symbol} {currency.name} ({currency.code})
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500">
                  La devise utilisée pour afficher les prix
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Configuration régionale */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration régionale</CardTitle>
              <CardDescription>
                Paramètres de langue et fuseau horaire
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="default_language" className="block text-sm font-medium text-gray-700">
                  Langue par défaut
                </label>
                <select
                  id="default_language"
                  name="default_language"
                  value={formData.default_language}
                  onChange={handleSelectChange}
                  disabled={(loading && !demoMode) || isSaving}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Sélectionner une langue</option>
                  {languageOptions.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500">
                  La langue d'affichage par défaut de l'application
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="timezone" className="block text-sm font-medium text-gray-700">
                  Fuseau horaire
                </label>
                <select
                  id="timezone"
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleSelectChange}
                  disabled={(loading && !demoMode) || isSaving}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Sélectionner un fuseau horaire</option>
                  {timezoneOptions.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500">
                  Le fuseau horaire utilisé pour les dates et heures
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Configuration des taxes */}
          <Card>
            <CardHeader>
              <CardTitle>Configuration des taxes</CardTitle>
              <CardDescription>
                Options de calcul des taxes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="tax_calculation" className="block text-sm font-medium text-gray-700">
                  Méthode de calcul des taxes
                </label>
                <select
                  id="tax_calculation"
                  name="tax_calculation"
                  value={formData.tax_calculation}
                  onChange={handleSelectChange}
                  disabled={loading || isSaving}
                  className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                >
                  <option value="">Sélectionner une méthode</option>
                  <option value="inclusive">Taxes incluses dans le prix</option>
                  <option value="exclusive">Taxes ajoutées au prix</option>
                </select>
                <p className="text-sm text-gray-500">
                  Détermine si les taxes sont incluses dans les prix affichés ou ajoutées à la caisse
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="tax_rate" className="block text-sm font-medium text-gray-700">
                  Taux de taxe par défaut (%)
                </label>
                <input
                  id="tax_rate"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  placeholder="Ex: 9.98"
                  disabled
                  value="9.98"
                  className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm"
                />
                <p className="text-sm text-gray-500">
                  Le taux de taxe par défaut est configuré dans la section "Taxes"
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Informations système */}
          <Card>
            <CardHeader>
              <CardTitle>Informations système</CardTitle>
              <CardDescription>
                Détails sur la configuration actuelle
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Dernière mise à jour</label>
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border">
                  {systemConfig ? 'Configuration chargée' : 'Non disponible'}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Pays disponibles</label>
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border">
                  {availableCountries.length} pays configurés
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">Devises disponibles</label>
                <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border">
                  {availableCurrencies.length} devises configurées
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button type="submit" disabled={loading || isSaving}>
            {isSaving ? (
              <>
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                Sauvegarde en cours...
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                Sauvegarder les paramètres
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleRefresh}
            disabled={loading || isSaving}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Actualiser
          </Button>

          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (systemConfig) {
                setFormData({
                  default_currency: systemConfig.default_currency || '',
                  default_country: systemConfig.default_country || '',
                  default_language: systemConfig.default_language || 'fr',
                  timezone: systemConfig.timezone || 'America/Toronto',
                  tax_calculation: systemConfig.tax_calculation || 'inclusive'
                })
              }
              setSaveSuccess(false)
              clearError()
            }}
            disabled={loading || isSaving}
          >
            Réinitialiser
          </Button>
        </div>
      </form>

      {/* Informations importantes */}
      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">Informations importantes</h3>
              <ul className="text-blue-800 space-y-2">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Les changements de configuration peuvent prendre quelques minutes pour être propagés à tous les utilisateurs</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Assurez-vous que le pays et la devise sélectionnés sont compatibles avec vos paramètres de taxe</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>La méthode de calcul des taxes affecte la façon dont les prix sont affichés aux clients</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Pour configurer des taux de taxe spécifiques, utilisez la section "Taxes" dans le menu de paramétrage</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {demoMode && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3" />
              <div>
                <h3 className="text-lg font-semibold text-yellow-900 mb-2">Configuration du backend</h3>
                <p className="text-yellow-800 mb-3">
                  Le service de configuration backend n'est pas disponible. Pour activer la configuration complète:
                </p>
                <ol className="text-yellow-800 space-y-2 list-decimal list-inside">
                  <li>Assurez-vous que le service de configuration est en cours d'exécution</li>
                  <li>Vérifiez que l'API est accessible à l'adresse <code className="bg-yellow-100 px-1 rounded">/api/config</code></li>
                  <li>Redémarrez l'application si nécessaire</li>
                  <li>Consultez la documentation pour configurer le service</li>
                </ol>
                <div className="mt-4">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Forcer une tentative de reconnexion
                      setDemoMode(false)
                      handleRefresh()
                    }}
                    className="text-yellow-800 border-yellow-300 hover:bg-yellow-100"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Réessayer la connexion
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default GeneralSettingsPage